import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PolitiqueConfidentialite() {
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-politique-confidentialite';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Politique de Confidentialité | Usine à Leads',
      description: "Politique de confidentialité d'Usine à Leads : collecte, utilisation et protection de vos données personnelles conformément au RGPD.",
      url: `${siteUrl}/politique-de-confidentialite`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/62fb907743b5e07417bde9cd3a46f134.png',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Politique de confidentialité', item: `${siteUrl}/politique-de-confidentialite` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  // Helper to safely get the current year (fallback for SSR)
  const currentYear = typeof new Date === 'function' ? new Date().getFullYear() : '';

  // Helper to format the last update date safely
  const formattedDate = (() => {
    try {
      return new Date().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  })();

  return (
    <div className="min-h-screen bg-white">
      <title>Politique de Confidentialité | Usine à Leads</title>
      <meta name="description" content="Consultez la politique de confidentialité d'Usine à Leads. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD." />
      <meta name="keywords" content="politique confidentialité, RGPD, données personnelles, usine à leads" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/politique-de-confidentialite`} />
      <meta property="og:title" content="Politique de Confidentialité | Usine à Leads" />
      <meta property="og:description" content="Politique de confidentialité d'Usine à Leads : collecte, utilisation et protection de vos données personnelles conformément au RGPD." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/politique-de-confidentialite`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav className="w-full bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/8eef813465f798e5a4134d50e400907e.png"
              alt="Usine à Leads"
              className="h-10 w-auto"
              loading="eager"
              decoding="async"
              width="140"
              height="40"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Politique de confidentialité
        </h1>
        <p className="text-sm text-gray-400 mb-10">
          Dernière mise à jour : {formattedDate}
        </p>
        <hr className="border-gray-100 mb-10" />

        <article className="space-y-10 text-gray-700 text-[15px] leading-relaxed">
          {/* Introduction */}
          <section>
            <p>
              La présente politique de confidentialité décrit la manière dont{' '}
              <strong>Usine à Leads</strong> (ci-après « nous », « notre ») collecte,
              utilise et protège les données personnelles des utilisateurs (ci-après
              « vous ») du site <strong>usineleads.com</strong> (ci-après « le Site »),
              conformément au Règlement Général sur la Protection des Données
              (RGPD) et à la loi Informatique et Libertés.
            </p>
          </section>

          {/* Responsable du traitement */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              1. Responsable du traitement
            </h2>
            <p>Le responsable du traitement des données personnelles est :</p>
            <div className="bg-gray-50 rounded-xl p-5 mt-4 border-l-4 border-orange-400 space-y-1 text-sm">
              <p>
                <strong>Usine à Leads</strong>
              </p>
              <p>Siège social : Paris, France</p>
              <p>Email : contact@usineleads.com</p>
              <p>Téléphone : +33 1 23 45 67 89</p>
            </div>
          </section>

          {/* Données collectées */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              2. Données personnelles collectées
            </h2>
            <p>Nous pouvons collecter les données suivantes :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données d&apos;identification :</strong> nom, prénom,
                  adresse email, numéro de téléphone, nom de l&apos;entreprise
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données de navigation :</strong> adresse IP,
                  type de navigateur, pages visitées, durée de visite, source de
                  trafic
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données de formulaire :</strong> toute information que
                  vous saisissez volontairement dans nos formulaires de contact
                  ou de demande de rendez-vous
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données de cookies :</strong> identifiants de session,
                  préférences de navigation, données analytiques
                </span>
              </li>
            </ul>
          </section>

          {/* Finalités */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              3. Finalités du traitement
            </h2>
            <p>Vos données personnelles sont collectées pour les finalités suivantes :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Répondre à vos demandes de contact et de rendez-vous</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Fournir et améliorer nos services de génération de leads</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Analyser l&apos;utilisation du Site pour en améliorer les performances</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Vous envoyer des communications commerciales (avec votre consentement)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Respecter nos obligations légales et réglementaires</span>
              </li>
            </ul>
          </section>

          {/* Base légale */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              4. Base légale du traitement
            </h2>
            <p>Le traitement de vos données repose sur :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Votre consentement</strong> (article 6.1.a du RGPD) : pour les
                  cookies analytiques et marketing, et les communications commerciales
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>L&apos;exécution d&apos;un contrat</strong> (article 6.1.b) : pour la
                  fourniture de nos services
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>L&apos;intérêt légitime</strong> (article 6.1.f) : pour l&apos;amélioration
                  de nos services et la sécurité du Site
                </span>
              </li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              5. Cookies et Google Consent Mode V2
            </h2>
            <p>
              Le Site utilise des cookies pour assurer son bon fonctionnement,
              analyser le trafic et personnaliser votre expérience. Nous utilisons{' '}
              <strong>Google Tag Manager</strong> avec le{' '}
              <strong>Google Consent Mode V2</strong> pour gérer le consentement aux
              cookies.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">
              Types de cookies utilisés
            </h3>

            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-settings-3-line text-orange-500"></i>
                  <span className="font-semibold text-gray-900 text-sm">
                    Cookies fonctionnels (obligatoires)
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Essentiels au fonctionnement du Site. Ils permettent la navigation et
                  l&apos;utilisation des fonctionnalités de base. Ne peuvent pas être
                  désactivés.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-bar-chart-line text-orange-500"></i>
                  <span className="font-semibold text-gray-900 text-sm">
                    Cookies analytiques (Google Analytics)
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Nous aident à comprendre comment les visiteurs interagissent avec le
                  Site. Les données sont anonymisées et utilisées uniquement à des fins
                  statistiques. Gérés via Google Tag Manager.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-megaphone-line text-orange-500"></i>
                  <span className="font-semibold text-gray-900 text-sm">
                    Cookies marketing
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Permettent de mesurer l&apos;efficacité de nos campagnes publicitaires
                  et d&apos;afficher des contenus personnalisés. Soumis à votre consentement
                  préalable.
                </p>
              </div>
            </div>

            <p className="mt-4">
              Vous pouvez modifier vos préférences de cookies à tout moment via la
              bannière de consentement accessible en bas de page.
            </p>
          </section>

          {/* Durée de conservation */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              6. Durée de conservation
            </h2>
            <p>Vos données personnelles sont conservées pendant les durées suivantes :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données de contact :</strong> 3 ans à compter du dernier contact
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données clients :</strong> durée de la relation contractuelle + 5
                  ans (obligations légales)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Cookies :</strong> 13 mois maximum conformément aux recommandations
                  de la CNIL
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Données de navigation :</strong> 26 mois maximum
                </span>
              </li>
            </ul>
          </section>

          {/* Destinataires */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              7. Destinataires des données
            </h2>
            <p>Vos données peuvent être transmises aux destinataires suivants :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Les membres de l&apos;équipe Usine à Leads habilités à traiter vos
                  demandes
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Nos sous-traitants techniques (hébergement, outils CRM, outils
                  d&apos;emailing)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Google (via Google Analytics et Google Tag Manager) pour
                  l&apos;analyse du trafic
                </span>
              </li>
            </ul>
            <p className="mt-3">Nous ne vendons jamais vos données personnelles à des tiers.</p>
          </section>

          {/* Transferts hors UE */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              8. Transferts hors Union européenne
            </h2>
            <p>
              Certains de nos sous-traitants peuvent être situés en dehors de
              l&apos;Union européenne. Dans ce cas, nous nous assurons que des
              garanties appropriées sont mises en place (clauses contractuelles
              types de la Commission européenne, décision d&apos;adéquation, etc.)
              conformément aux articles 44 et suivants du RGPD.
            </p>
          </section>

          {/* Droits */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Vos droits</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit d&apos;accès :</strong> obtenir la confirmation que vos
                  données sont traitées et en obtenir une copie
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit de rectification :</strong> corriger vos données
                  inexactes ou incomplètes
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit à l&apos;effacement :</strong> demander la suppression
                  de vos données
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit à la limitation :</strong> limiter le traitement de vos
                  données
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit à la portabilité :</strong> recevoir vos données dans un
                  format structuré
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit d&apos;opposition :</strong> vous opposer au traitement de
                  vos données
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-shield-check-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Droit de retrait du consentement :</strong> retirer votre
                  consentement à tout moment
                </span>
              </li>
            </ul>
            <p className="mt-4">
              Pour exercer ces droits, contactez-nous à :{' '}
              <a
                href="mailto:contact@usineleads.com"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                contact@usineleads.com
              </a>
            </p>
            <p className="mt-3">
              Vous disposez également du droit d&apos;introduire une réclamation
              auprès de la{' '}
              <a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                CNIL
              </a>
              .
            </p>
          </section>

          {/* Sécurité */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Sécurité des données</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles
              appropriées pour protéger vos données personnelles contre tout accès
              non autorisé, modification, divulgation ou destruction. Ces mesures
              incluent le chiffrement SSL, la limitation des accès et la sauvegarde
              régulière des données.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              11. Modifications de la politique
            </h2>
            <p>
              Nous nous réservons le droit de modifier la présente politique de
              confidentialité à tout moment. Toute modification sera publiée sur
              cette page avec une date de mise à jour. Nous vous encourageons à
              consulter régulièrement cette page.
            </p>
          </section>
        </article>
      </main>

      {/* Footer minimal */}
      <footer className="border-t border-gray-100 py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-400">
          <p>© {currentYear} Usine à Leads. Tous droits réservés.</p>
          <nav className="flex gap-6">
            <Link
              to="/mentions-legales"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-de-confidentialite"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap font-medium text-gray-600"
            >
              Politique de confidentialité
            </Link>
            <Link
              to="/cgv"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              CGV
            </Link>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Web Design
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
